import { Money } from '@libs/money/money.interfaces';
import {
    FlightPax,
    GordianSeating,
    ProductType,
    SeatingProduct,
} from '@/api/Cart/cart.interfaces';
import { ReducerStatus } from '@/store/types';
import { Worth } from '@shared/interfaces/preCheckout';

export interface MyBookingSessionEndPoint {
    GetBookingSession(bookingId: string, password: string): Promise<any>;
    SaveBookingSession(
        bookingId: string,
        password: string,
        addons: AnyUpsellAddon[]
    ): Promise<SaveMyBookingSession>;
    ClearBookingSession(): Promise<void>;
    SendSpecialRequestMessage(
        bookingId: string,
        password: string,
        message: string,
        messageCategory: string
    ): Promise<void>;
}

export interface MyBookingSessionData {
    AvailableAddons: AnyUpsellAddon[];
}

export enum ResponseStatus {
    Error = 'error',
    Redirect = 'redirect',
    BookingSuccess = 'booking-success',
}

export interface SaveMyBookingSessionResponse {
    Errors: string[];
}

export interface SaveMyBookingSession {
    errors: string[];
    status: ResponseStatus;
}

export type AnyUpsellAddon =
    | UpSellAddonSeating
    | UpSellAddonBaggage
    | UpSellAddonFerry
    | UpSellAddonServicePackage
    | UpSellAddonTravelInsurance;

interface UpSellAddon extends UpSellAddonExtras {
    Price: Money | null;
    PriceOptions: PriceOptions | null;
    Type: AvailableAddonType;
}

interface UpSellAddonExtras {
    Id?: string;
    Status?: ReducerStatus | undefined;
}

export interface UpSellAddonSeating extends UpSellAddon {
    Pnr: string;
    Type: AvailableAddonType.Seating;
    ProductType: ProductType.Flight;
    GordianSession: GordianSeating;
    SelectedSeats?: SeatingProduct[] | null;
}

export interface UpSellAddonBaggage extends UpSellAddon, UpSellAddonBaggageExtras {
    Pnr: string;
    Type: AvailableAddonType.Baggage;
    ProductType: ProductType.Flight;
    Pax: FlightPax[];
}

export interface UpSellAddonTravelInsurance extends UpSellAddon {
    Type: AvailableAddonType.TravelInsurance;
    ProductType: ProductType.Other;
    Coverage: string;
}

export interface UpSellAddonFerry extends UpSellAddon {
    Category?: Category | null;
    Description?: string | null;
    Id: string;
    InfoMessages?: string[] | null;
    IsCancellable?: boolean;
    ProductId: number;
    ProductType: ProductType.Ferry;
    SupplierReference: string;
    Title?: string;
    Type: FerryAddonTypes;
    Validations: Validation[] | null;
    Value?: number;
    Worth: Worth | null;
}

export type FerryAddonTypes =
    | AvailableAddonType.Meal
    | AvailableAddonType.Seat
    | AvailableAddonType.Pet
    | AvailableAddonType.Cabin
    | AvailableAddonType.DeckSpace
    | AvailableAddonType.Lounge;

interface UpSellAddonBaggageExtras {
    InitialPax?: FlightPax[];
}

export interface UpSellAddonServicePackage extends UpSellAddon {
    Type: AvailableAddonType.ServicePackage;
    ProductType: ProductType.Other;
    PackageType: ServicePackageType;
}

export enum ServicePackageType {
    Basic = 'Basic',
    Premium = 'Premium',
}

export interface Validation {
    Value: any;
    ValidationType: ValidationType | null;
    Message: string | null;
}

export enum ValidationType {
    Exact = 'Exact',
    Minimum = 'Minimum',
    Maximum = 'Maximum',
}

export enum Category {
    Cabin = 'Cabin',
    Seat = 'Seat',
    Lounge = 'Lounge',
    Kennel = 'Kennel',
}

// TODO: move enum to a more suitable place
export enum UpSellType {
    Seating = 'Seating',
    Baggage = 'Baggage',
    TravelInsurance = 'TravelInsurance',
    ServicePackage = 'ServicePackage',
    FerryAddons = 'FerryAddons',
    RentalCar = 'RentalCar',
    RentalCarWidget = 'RentalCarWidget',
    Hotel = 'Hotel',
    HotelWidget = 'HotelWidget',
    Ferry = 'Ferry',
    Transfer = 'Transfer',
    Tickets = 'Tickets',
    TripWidget = 'TripWidget',
}

// Used to display price in correct "format", ex fr. 399 kr /day
interface PriceOptions {
    PerDay: boolean;
    From: boolean;
}

export enum AvailableAddonType {
    Undefined = 'Undefined',
    Seating = 'Seating',
    Baggage = 'Baggage',
    ServicePackage = 'ServicePackage',
    RentalCar = 'RentalCar',
    Lounge = 'Lounge',
    Cabin = 'Cabin',
    Seat = 'Seat',
    DeckSpace = 'DeckSpace',
    SkiSet = 'SkiSet',
    Transfer = 'Transfer',
    TravelInsurance = 'TravelInsurance',
    Tickets = 'Tickets',
    Meal = 'Meal',
    Pet = 'Pet',
}
